/**
 * Time Indicator Component
 *
 * Visual indicator for worklist item urgency based on time remaining.
 * Shows color-coded badge with time remaining or "Vencido" status.
 *
 * Color scheme:
 * - Green: >50% time remaining
 * - Yellow: 25-50% time remaining
 * - Red: <25% time remaining
 * - Gray/Dark: Expired/overdue
 */

import { useMemo } from 'react'
import { Clock, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  calculateTimeIndicator,
  getTimeIndicatorLabel,
  getTimeIndicatorShortLabel,
  type TimeIndicatorColor,
} from '@/domain/worklist/time-utils'

// =============================================================================
// Types
// =============================================================================

interface TimeIndicatorProps {
  /** Due date for the worklist item */
  dueAt: string | Date | null
  /** SLA hours configured for the institution (default: 48) */
  slaHours?: number
  /** When the worklist item was created (for accurate percentage) */
  createdAt?: string | Date | null
  /** Display variant: 'badge' for compact, 'detailed' for full info */
  variant?: 'badge' | 'detailed'
  /** Show icon */
  showIcon?: boolean
  /** Additional CSS classes */
  className?: string
}

// =============================================================================
// Styles
// =============================================================================

const colorStyles: Record<TimeIndicatorColor, string> = {
  green: 'bg-emerald-500 text-white border-emerald-600',
  yellow: 'bg-amber-500 text-white border-amber-600',
  red: 'bg-red-500 text-white border-red-600',
  expired: 'bg-gray-700 text-white border-gray-800',
}

const iconColorStyles: Record<TimeIndicatorColor, string> = {
  green: 'text-white',
  yellow: 'text-white',
  red: 'text-white',
  expired: 'text-white',
}

// =============================================================================
// Component
// =============================================================================

export function TimeIndicator({
  dueAt,
  slaHours = 48,
  createdAt,
  variant = 'badge',
  showIcon = true,
  className,
}: TimeIndicatorProps) {
  const indicator = useMemo(
    () => calculateTimeIndicator(dueAt, slaHours, createdAt),
    [dueAt, slaHours, createdAt]
  )

  const label = useMemo(
    () => (variant === 'badge' ? getTimeIndicatorShortLabel(indicator) : getTimeIndicatorLabel(indicator)),
    [indicator, variant]
  )

  const Icon = indicator.isOverdue ? AlertTriangle : Clock

  if (variant === 'badge') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
          colorStyles[indicator.color],
          className
        )}
        title={getTimeIndicatorLabel(indicator)}
      >
        {showIcon && <Icon className={cn('h-3 w-3', iconColorStyles[indicator.color])} />}
        <span>{label}</span>
      </span>
    )
  }

  // Detailed variant
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 rounded-md border px-3 py-1.5',
        colorStyles[indicator.color],
        className
      )}
    >
      {showIcon && <Icon className={cn('h-4 w-4', iconColorStyles[indicator.color])} />}
      <div className="flex flex-col">
        <span className="text-sm font-medium">{label}</span>
        {!indicator.isOverdue && (
          <span className="text-xs opacity-75">
            {Math.round(indicator.percentRemaining)}% restante
          </span>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// Subcomponents
// =============================================================================

/**
 * Progress bar variant of time indicator
 */
interface TimeIndicatorProgressProps {
  dueAt: string | Date | null
  slaHours?: number
  createdAt?: string | Date | null
  className?: string
}

export function TimeIndicatorProgress({
  dueAt,
  slaHours = 48,
  createdAt,
  className,
}: TimeIndicatorProgressProps) {
  const indicator = useMemo(
    () => calculateTimeIndicator(dueAt, slaHours, createdAt),
    [dueAt, slaHours, createdAt]
  )

  const progressBarColors: Record<TimeIndicatorColor, string> = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    expired: 'bg-gray-600',
  }

  return (
    <div className={cn('w-full', className)}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-[var(--foreground-muted)]">
          {getTimeIndicatorLabel(indicator)}
        </span>
        <span className="text-xs font-medium">
          {indicator.isOverdue ? 'Vencido' : `${Math.round(indicator.percentRemaining)}%`}
        </span>
      </div>
      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-300',
            progressBarColors[indicator.color]
          )}
          style={{ width: `${Math.max(indicator.percentRemaining, 0)}%` }}
        />
      </div>
    </div>
  )
}
